import { Collapse, Grid } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormAutocomplete, FormDateTimePicker, FormNumberInput, FormTextField } from "../../../../components/fields";
import FilterActions from "../../../../components/filter/filter-actions/FilterActions";
import FilterWrapper from "../../../../components/filter/filter-wrapper/FilterWrapper";
import { ReactQueryKey } from "../../../../types";
import { bookingsFilterState } from "../../../search/recoil";
import { IBookingsRceDashboardApiFilter, IFormValuesBookingsFilter } from "../../types";
import { LOCALE_DATE_FORMAT, LOCALE_ISO_DATE_TIME_FORMAT } from "../../../../utils";
import { FormAcrissList, FormPickupLocations, FormSippList } from "./fields";
import { useQuery } from "@tanstack/react-query";
import { getBookingAcrissList, getBookingCategories, getBookingDomains, getBookingLocations, getBookingSegments, getBookingSippList } from "../../../../api/services";
import TenantForm from "../../../../components/multitenant/TenantForm";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import DealerForm from "../../../../components/multitenant/DealerForm";
import { IBookingsApiFilter } from "../../../../api/types";

const FilterBookings = () => {

    const [searchParams] = useSearchParams();

    const { t } = useTranslation('booking');
    const { globalData } = useContext(GlobalContext);

    const setBookingsFilter = useSetRecoilState(bookingsFilterState);

    const domainsQuery = useQuery({
        queryKey: [ReactQueryKey.BookingDomains],
        queryFn: getBookingDomains,
        enabled: false
    });

    const validationSchema = yup.object({
        pickupDate: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable(),
        dropoffDateFrom: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testDropoffDateFrom",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.dropoffDateTo ||
                        value <= context.parent.dropoffDateTo
                    );
                }
            ),
        dropoffDateTo: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testDropoffDateTo",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.dropoffDateFrom ||
                        value >= context.parent.dropoffDateFrom
                    );
                }
            ),
        bookingDateFrom: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testBookingDateFrom",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.bookingDateTo ||
                        value <= context.parent.bookingDateTo
                    );
                }
            ),
        bookingDateTo: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testBookingDateFrom",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.bookingDateFrom ||
                        value >= context.parent.bookingDateFrom
                    );
                }
            ),
        tenants: yup
            .array()
            .min(1, "message.validation.required")
    });


    const form = useForm<IBookingsRceDashboardApiFilter>({
        defaultValues: {
            bookingNumber: '',
            bookingDateFrom: null,
            bookingDateTo: null,
            categories: [],
            customerTypes: [],
            bookingOwnerFirstName: '',
            bookingOwnerLastName: '',
            bookingOwnerCompanyName: '',
            driverFirstName: '',
            driverLastName: '',
            pickupLocations: [],
            dropoffLocations: [],
            pickupDate: null,
            dropoffDateFrom: null,
            dropoffDateTo: null,
            rentalDaysFrom: 0,
            rentalDaysTo: 0,
            acrissList: [],
            sippList: [],
            tenants: globalData?.user.legalEntities?.map((elem: any) => elem.name),
            dealers: globalData?.user.dealers?.map((elem: any) => elem.name)
        },
        resolver: yupResolver(validationSchema, {}, { rawValues: true })
    });

    const [expanded, setExpanded] = useState<boolean>(false);

    const tenantWatch = useWatch<IBookingsRceDashboardApiFilter, 'tenants'>({
        control: form.control,
        name: "tenants"
    });

    const dealersWatch = useWatch<IBookingsRceDashboardApiFilter, 'dealers'>({
        control: form.control,
        name: "dealers"
    });

    const locationsQuery = useQuery(
        [ReactQueryKey.BookingLocations, { tenants: tenantWatch, dealers: dealersWatch }],
        () => getBookingLocations({ tenants: tenantWatch, dealers: dealersWatch ?? [] }),
    );

    const acrissListQuery = useQuery(
        [ReactQueryKey.BookingAcrissList, { tenants: tenantWatch, dealers: dealersWatch }],
        () => getBookingAcrissList({ tenants: tenantWatch, dealers: dealersWatch ?? [] }),
    );

    const sippListQuery = useQuery(
        [ReactQueryKey.BookingSippList, { tenants: tenantWatch, dealersWatch }],
        () => getBookingSippList({ tenants: tenantWatch, dealers: dealersWatch ?? [] }),
    );

    const segmentsQuery = useQuery(
        [ReactQueryKey.BookingSegments, { tenants: tenantWatch, dealersWatch }],
        () => getBookingSegments({ tenants: tenantWatch, dealers: dealersWatch ?? [] }),
    );

    const categoriesQuery = useQuery(
        [ReactQueryKey.BookingCategories, { tenants: tenantWatch, dealersWatch }],
        () => getBookingCategories({ tenants: tenantWatch, dealers: dealersWatch ?? [] }),
    );


    const createBookingsFilter = useCallback((data: IBookingsRceDashboardApiFilter): IBookingsApiFilter => ({
        bookingNumber: data.bookingNumber || null,
        pickupLocations: data.pickupLocations.map(pl => pl.id),
        dropoffLocations: data.dropoffLocations.map(dl => dl.id),
        pickupDate: data.pickupDate?.format(LOCALE_DATE_FORMAT) || null,
        dropoffDateFrom: data.dropoffDateFrom?.format(LOCALE_ISO_DATE_TIME_FORMAT) || null,
        dropoffDateTo: data.dropoffDateTo?.format(LOCALE_ISO_DATE_TIME_FORMAT) || null,
        bookingDateFrom: data.bookingDateFrom?.toISOString() || null,
        bookingDateTo: data.bookingDateTo?.toISOString() || null,
        rentalDaysFrom: data.rentalDaysFrom || null,
        rentalDaysTo: data.rentalDaysTo || null,
        driverFirstName: data.driverFirstName || null,
        driverLastName: data.driverLastName || null,
        bookingOwnerFirstName: data.bookingOwnerFirstName || null,
        bookingOwnerLastName: data.bookingOwnerLastName || null,
        bookingOwnerCompanyName: data.bookingOwnerCompanyName || null,
        acrissList: data.acrissList.map(a => a),
        sippList: data.sippList.map(s => s),
        segments: data.customerTypes.map(s => s.code),
        categories: data.categories.map(p => p.code),
        tenants: data.tenants || [],
        dealers: data.dealers
    }), []);

    const handleSearch = useCallback((data: IBookingsRceDashboardApiFilter) => {
        setBookingsFilter(createBookingsFilter(data));
        setExpanded(false);
    }, []);
    const handleReset = useCallback(() => {
        form.reset();
        setBookingsFilter(createBookingsFilter(form.getValues()));
        setExpanded(false);
    }, []);

    const handleToggle = useCallback(() => {
        setExpanded(current => !current);
    }, [setExpanded]);

    useEffect(() => {

        if (Array.from(searchParams).length == 0) return;
        domainsQuery.refetch();

    }, [searchParams])

    return (
        <FilterWrapper>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(handleSearch)}>
                    <Grid container columnSpacing={5} alignItems="center">
                        <Grid item xs={12} md={6} lg={3}>
                            <FormTextField<IBookingsRceDashboardApiFilter>
                                name="bookingNumber"
                                label={t('label.bookingNumber')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <FormPickupLocations />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <FormDateTimePicker<IBookingsRceDashboardApiFilter>
                                name="pickupDate"
                                label={t('label.pickupDate')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <FilterActions
                                expanded={expanded}
                                onSearch={form.handleSubmit(handleSearch)}
                                onReset={handleReset}
                                onToggle={handleToggle}
                            />
                        </Grid>
                    </Grid>
                    <Collapse in={expanded} timeout="auto" sx={{ width: '100%' }}>
                        <Grid container columnSpacing={5} rowSpacing={2} pt={2}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<IBookingsRceDashboardApiFilter>
                                    name="categories"
                                    label={t('label.categories')}
                                    options={categoriesQuery.data ?? []}
                                    multiple
                                    isOptionEqualToValue={(option, value) => option.code === value.code}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                {/* <FormDropoffLocations /> */}
                                <FormAutocomplete<IBookingsRceDashboardApiFilter>
                                    name="dropoffLocations"
                                    label={t('label.dropoffLocations')}
                                    options={locationsQuery.data?.locations ?? []}
                                    loading={locationsQuery.isLoading}
                                    multiple
                                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                    getOptionLabel={(option) => option.label}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IBookingsRceDashboardApiFilter>
                                    name="dropoffDateFrom"
                                    label={t('label.dropoffDateFrom')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IBookingsRceDashboardApiFilter>
                                    name="dropoffDateTo"
                                    label={t('label.dropoffDateTo')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormAcrissList />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormSippList />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IBookingsRceDashboardApiFilter>
                                    name="driverFirstName"
                                    label={t('label.mainDriverFirstName')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IBookingsRceDashboardApiFilter>
                                    name="driverLastName"
                                    label={t('label.mainDriverLastName')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormNumberInput<IBookingsRceDashboardApiFilter>
                                    name="rentalDaysFrom"
                                    label={t('label.rentalDaysFrom')}
                                    minValue={0}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormNumberInput<IBookingsRceDashboardApiFilter>
                                    name="rentalDaysTo"
                                    label={t('label.rentalDaysTo')}
                                    minValue={0}
                                />
                            </Grid>

                            <Grid item xs={12} md={6} lg={3}>
                                {/* <FormSegments /> */}
                                <FormAutocomplete<IBookingsRceDashboardApiFilter>
                                    name="customerTypes"
                                    label={t('label.segments')}
                                    options={segmentsQuery.data ?? []}
                                    multiple
                                    isOptionEqualToValue={(option, value) => option.code === value.code}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IBookingsRceDashboardApiFilter>
                                    name="bookingOwnerCompanyName"
                                    label={t('label.bookingOwnerCompanyName')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IBookingsRceDashboardApiFilter>
                                    name="bookingOwnerFirstName"
                                    label={t('label.bookingOwnerFirstName')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IBookingsRceDashboardApiFilter>
                                    name="bookingOwnerLastName"
                                    label={t('label.bookingOwnerLastName')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IBookingsRceDashboardApiFilter>
                                    name="bookingDateFrom"
                                    label={t('label.creationDateFrom')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IBookingsRceDashboardApiFilter>
                                    name="bookingDateTo"
                                    label={t('label.creationDateTo')}
                                />
                            </Grid>
                            {!(globalData?.user.legalEntities.length === 1 && globalData?.user.dealers.length === 1) &&
                                <Grid item xs={12} md={6} lg={3}>
                                    <TenantForm
                                        control={form.control}
                                        name="tenant"
                                        onChange={() => form.setValue("dealers", [])}
                                    />
                                </Grid>
                            }
                            {!(globalData?.user.legalEntities.length === 1 && globalData?.user.dealers.length === 1) &&
                                <Grid item xs={12} md={6} lg={3}>
                                    <DealerForm
                                        control={form.control}
                                        name="dealer"
                                        tenants={tenantWatch}
                                    />
                                </Grid>
                            }
                        </Grid>
                    </Collapse>
                </form>
            </FormProvider>
        </FilterWrapper>
    );
};

export default FilterBookings;
